import Button from '@/gf/components/ButtonOld'
import Card from '@/gf/components/Card'
import Modal from '@/gf/components/Modal'
import Spinner from '@/gf/components/Spinner'
import { Table, Tbody, Td, Th, Thead, Tr } from '@/gf/components/Table'
import TextField from '@/gf/components/TextField'
import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import { useUpdateUserNotifyMutation } from '@/suppliers/_gen/gql'
import useGqlClient from '@/suppliers/hooks/useGqlClient'
import * as api from '@/suppliers/modules/api'
import { useMutation } from '@apollo/client'
import { yupResolver } from '@/gf/lib/yupResolver'
import { GraphQLError } from 'graphql'
import React from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { ProfileData, Store, User } from '../../../types'
import INVITE_USER_TO_STORE from '../../mutations/store'

type UserForm = {
  name: string
  email: string
  storeId: string
  phone: string
}

const UserSchema = yup
  .object({
    name: yup.string().required().label('Full Name'),
    email: yup.string().required().label('Email Address'),
    phone: yup.string().required().label('Phone Number'),
  })
  .required()

interface NotifyModalProps {
  selected: User
  refetch: () => Promise<void>
  open: boolean
  onClose: () => void
}

const NotifyMessagingModal: React.FC<NotifyModalProps> = ({ selected, refetch, open, onClose }) => {
  const [spinnerLive, spinner] = useToggle()
  const [_msgs, msgsMgr] = useMsgs()
  const gqlClient = useGqlClient()
  const [updateUserNotify] = useUpdateUserNotifyMutation({ client: gqlClient })

  const notifyUpdate = () => {
    spinner.on()

    updateUserNotify({
      variables: { userId: selected.id },
    })
      .then(() => {
        msgsMgr.add('Notifications updated!', 'positive')

        return refetch().then(() => Promise.resolve().then(onClose))
      })
      .catch((err: GraphQLError) => {
        msgsMgr.add(err.message, 'negative')

        onClose()
      })
      .finally(() => {
        spinner.off()
      })
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Update Notify Settings"
      footerAction={<Button title="Confirm" onClick={notifyUpdate} disabled={spinnerLive} />}
    >
      {spinnerLive ? (
        <Spinner />
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            notifyUpdate()
          }}
        >
          {selected && (
            <p className="mb-2">Adjust the notification settings for {selected.email}</p>
          )}
        </form>
      )}
    </Modal>
  )
}

const UsersSettings = ({ store }: { store: Store }) => {
  const [_msgs, msgsMgr] = useMsgs()
  const [selected, setSelected] = React.useState<User>()
  const [activeUsers, setActiveUsers] = React.useState(store.users)

  const [inviteUserToStore] = useMutation(INVITE_USER_TO_STORE)

  const form = useForm<UserForm>({
    shouldUnregister: true,
    defaultValues: { storeId: store.id },
    resolver: yupResolver(UserSchema),
  })

  const onInviteSubmit: SubmitHandler<ProfileData> = (data) => {
    inviteUserToStore({
      variables: {
        email: data.email,
        name: data.name,
        phone: data.phone,
      },
    })
      .then(() => {
        msgsMgr.add('User added to store!', 'positive')

        return api.fetchStore(store.id).then((theStore) => {
          setActiveUsers(theStore.users)
        })
      })
      .catch((error: GraphQLError) => {
        if (error.message) msgsMgr.add(error.message, 'negative')
        else msgsMgr.add('Error adding user to store', 'negative')
      })
      .finally(form.reset)
  }

  const updateNotificationSettings = (user: User) => {
    setSelected(user)
  }

  const refetch = async () => {
    await api.fetchStore(store.id).then((theStore) => {
      setActiveUsers(theStore.users)
    })
  }

  return (
    <Card title="Active Users">
      {selected && (
        <NotifyMessagingModal
          selected={selected}
          refetch={refetch}
          open={!!selected}
          onClose={() => setSelected(undefined)}
        />
      )}
      <Card.Section>
        <Table className="rounded border border-gray-300">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Notify</Th>
            </Tr>
          </Thead>
          <Tbody>
            {activeUsers.map((user) => (
              <Tr key={user.id}>
                <Td>{user.confirmed ? user.name || 'No name' : 'Invited User'}</Td>
                <Td>{user.email}</Td>
                <Td>
                  <input
                    className="rounded"
                    type="checkbox"
                    checked={user.notify === true}
                    onClick={(event) => {
                      event.stopPropagation()
                    }}
                    onChange={() => updateNotificationSettings(user)}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Card.Section>
      <Card.Section title="Invite Users">
        <form className="space-y-4" onSubmit={form.handleSubmit(onInviteSubmit)}>
          <Card.SubSection>
            <div className="space-y-4">
              <TextField
                label="Full Name"
                errors={form.formState.errors.name?.message}
                {...form.register('name')}
              />
              <TextField
                label="Email Address"
                errors={form.formState.errors.email?.message}
                {...form.register('email')}
              />
              <TextField
                label="Phone Number"
                errors={form.formState.errors.phone?.message}
                {...form.register('phone')}
              />
            </div>
          </Card.SubSection>
          <div className="flex justify-end rounded-b-md">
            <Button type="submit" title="Invite" />
          </div>
        </form>
      </Card.Section>
    </Card>
  )
}

export default UsersSettings
